import { useTranslate } from "react-admin";

import EnumRiskLevel from "@wallet-manager/node-types/dist/types/postgres/const/RiskLevel";

import { Box } from "../MuiGenerals";

const RISK_COLOR = {
  red: {
    bg: "#FFF1F0",
    border: "#FFA39E",
    text: "#CF1322",
  },
  blue: {
    bg: "#E6F4FF",
    border: "#91CAFF",
    text: "#0958D9",
  },
  grey: {
    bg: "#F5F5F5",
    border: "#D9D9D9",
    text: "#595959",
  },
} as const;

const getRiskColorKey = (riskLevel: number): keyof typeof RISK_COLOR => {
  if (riskLevel === 5) return "red";
  if (riskLevel === -2 || riskLevel === -1) return "grey";
  return "blue";
};

const stripPrefix = (label: string): string => {
  // enumConstants labels look like "5 Critical"; strip the numeric prefix so the
  // badge can show its own count separately (matches the spec's "5 Critical" badge).
  return label.replace(/^-?\d+\s+/, "");
};

interface RiskLevelBadgeProps {
  riskLevel: number | string | null | undefined;
  size?: "small" | "medium";
}

export default function RiskLevelBadge({
  riskLevel,
  size = "small",
}: RiskLevelBadgeProps) {
  const translate = useTranslate();
  const numericRisk = Number(riskLevel);

  if (
    riskLevel === null ||
    riskLevel === undefined ||
    Number.isNaN(numericRisk)
  ) {
    return <span />;
  }

  const enumLabel = EnumRiskLevel[numericRisk];
  const translated = enumLabel
    ? translate(`enumConstants.${enumLabel}`)
    : String(riskLevel);

  const parts = translated.match(/^(-?\d+)\s+(.*)$/);
  const countPart = parts?.[1] ?? "";
  const textPart = parts ? parts[2] : stripPrefix(translated);

  const palette = RISK_COLOR[getRiskColorKey(numericRisk)];

  const padding = size === "small" ? "1px 6px" : "2px 10px";
  const fontSize = size === "small" ? "11px" : "13px";

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        backgroundColor: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.text,
        borderRadius: "4px",
        padding,
        fontSize,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontWeight: 600 }}>{countPart}</span>
      <span>{textPart}</span>
    </Box>
  );
}
